import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { employerVerificationDashboardApi } from '../../api/employerVerificationDashboardApi';
import {
  buildEmploymentSubmittedDisplayRows,
  employmentEvidenceNeedsDetailFetch,
} from '../../lib/employment/employmentSubmittedDisplay';
import { extractReviewSignalBundle, readRequestSnapshotFromEvidence } from '../../lib/employment';
import { countReviewSignalEntries } from '../../lib/employment/reviewSignalPresentation';
import { EmploymentSectionAccordion } from './EmploymentSectionAccordion';
import { EmployerAttemptSubmittedFieldsTable } from './EmployerAttemptSubmittedFields';
import { ReviewSignalBundlePanel } from './ReviewSignalBundlePanel';
import type { ReviewSignalBundle } from '../../lib/employment/reviewSignalBundle.types';

type Props = {
  row: Record<string, unknown>;
  evidence?: unknown;
  bundle?: ReviewSignalBundle | null;
  lastActionResult?: Record<string, unknown> | null;
  defaultExpanded?: boolean;
  isLatest?: boolean;
};

function readVerificationId(row: Record<string, unknown>): string | null {
  return typeof row.id === 'string' && row.id.trim() ? row.id.trim() : null;
}

function readAttemptNumber(row: Record<string, unknown>): number | null {
  return typeof row.attemptNumber === 'number' && Number.isFinite(row.attemptNumber)
    ? row.attemptNumber
    : null;
}

/** One drawer per attempt: customer submission + review signals (no duplicate top-level sections). */
export function EmployerAttemptDetailAccordion({
  row,
  evidence: evidenceProp,
  bundle: bundleProp,
  lastActionResult,
  defaultExpanded = false,
  isLatest = false,
}: Props) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const verificationId = readVerificationId(row);
  const attemptNumber = readAttemptNumber(row);
  const verifiedAt = row.verifiedAt != null && row.verifiedAt !== '' ? String(row.verifiedAt) : null;

  const listEvidence =
    row.evidence != null && typeof row.evidence === 'object' && !Array.isArray(row.evidence)
      ? row.evidence
      : undefined;

  const resolvedPropEvidence = evidenceProp ?? listEvidence;
  const needsFetch =
    expanded &&
    !!verificationId &&
    (!resolvedPropEvidence || employmentEvidenceNeedsDetailFetch(resolvedPropEvidence));

  const detailQ = useQuery({
    queryKey: ['employerVerificationDetail', verificationId],
    queryFn: () => employerVerificationDashboardApi.detail(verificationId!),
    enabled: needsFetch,
    staleTime: 60_000,
  });

  const evidence = evidenceProp ?? detailQ.data?.evidence ?? listEvidence;
  const snapshot = readRequestSnapshotFromEvidence(evidence);
  const rows = expanded
    ? buildEmploymentSubmittedDisplayRows({ snapshot, listRow: row, evidence })
    : buildEmploymentSubmittedDisplayRows({
        snapshot: readRequestSnapshotFromEvidence(resolvedPropEvidence),
        listRow: row,
        evidence: resolvedPropEvidence,
      });

  const bundle =
    bundleProp ??
    extractReviewSignalBundle({
      evidence,
      lastActionResult: isLatest ? lastActionResult ?? null : null,
    });

  const reviewCount = bundle ? countReviewSignalEntries(bundle) : 0;
  const fieldHint = expanded && rows.length > 0 ? `${rows.length} fields` : null;
  const reviewHint = reviewCount > 0 ? `${reviewCount} review item${reviewCount === 1 ? '' : 's'}` : null;
  const summaryParts = [fieldHint, reviewHint].filter(Boolean);

  const attemptLabel =
    attemptNumber != null
      ? `Attempt ${attemptNumber}${isLatest ? ' (latest)' : ''}`
      : isLatest
        ? 'Latest'
        : 'Prior';

  const title =
    summaryParts.length > 0
      ? `${attemptLabel} — ${summaryParts.join(' · ')}`
      : attemptLabel;

  const subtitle = verifiedAt
    ? new Date(verifiedAt).toLocaleString()
    : 'Expand for submission fields and review signals';

  return (
    <EmploymentSectionAccordion
      title={title}
      subtitle={subtitle}
      expanded={expanded}
      onExpandedChange={setExpanded}
      testId={`employment-attempt-detail-${attemptNumber ?? 'na'}`}
    >
      {detailQ.isPending && expanded ? (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 1.5 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading attempt details…
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Stack spacing={0.75}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Customer submission
            </Typography>
            <EmployerAttemptSubmittedFieldsTable
              snapshot={snapshot}
              listRow={row}
              evidence={evidence}
              attemptNumber={attemptNumber}
              verifiedAt={verifiedAt}
              showIntro={false}
            />
          </Stack>

          {bundle && reviewCount > 0 ? (
            <>
              <Divider />
              <Stack spacing={0.75}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  What needs review
                </Typography>
                <ReviewSignalBundlePanel
                  bundle={bundle}
                  attemptNumber={attemptNumber ?? undefined}
                  variant="compact"
                  hideHeaderChips
                  lastActionResult={isLatest ? lastActionResult : undefined}
                />
              </Stack>
            </>
          ) : null}
        </Stack>
      )}
    </EmploymentSectionAccordion>
  );
}
